import React, { useState, useEffect } from "react";
import { useMediaQuery } from "react-responsive";
import { Grid, Box, Typography, Button } from "@mui/material";
import { Link } from "react-router-dom";
import { COLORS } from "../../constants";
import "./style.css";
import { Container } from "@mui/system";
import useSystemHook from "./../../hooks/SystemHook";

const DealOfTheWeek = () => {
  const { isMobile, isTablet, isDesktop } = useSystemHook();

  // Simulate API data
  const [dealData, setDealData] = useState({
    specialImage: "/images/specialImage.png", // Default image path
    productDescription: "Alternator - Alt001",
  });

  // Simulate fetching data from API
  useEffect(() => {
    // This could be an API call like: fetch('/api/deal-of-the-week').then(...)
    // For now, we'll just set the state with static data
    setDealData({
      specialImage: "/images/specialImage.png",
      productDescription: "Alternator - Alt001",
    });
  }, []);

  // Countdown Timer Logic
  const calculateTimeLeft = () => {
    // Set a deadline 7 days from the current date (June 06, 2025)
    const deadline = new Date("2025-09-31T23:59:59"); // September 31, 2025, 11:59:59 PM SAST
    const now = new Date();
    const difference = deadline - now;

    let timeLeft = {};

    if (difference > 0) {
      timeLeft = {
        days: Math.floor(difference / (1000 * 60 * 60 * 24)),
        hours: Math.floor((difference / (1000 * 60 * 60)) % 24),
        minutes: Math.floor((difference / 1000 / 60) % 60),
        seconds: Math.floor((difference / 1000) % 60),
      };
    } else {
      timeLeft = { days: 0, hours: 0, minutes: 0, seconds: 0 };
    }

    return timeLeft;
  };

  const [timeLeft, setTimeLeft] = useState(calculateTimeLeft());

  useEffect(() => {
    const timer = setInterval(() => {
      setTimeLeft(calculateTimeLeft());
    }, 1000);

    return () => clearInterval(timer); // Cleanup on unmount
  }, []);

  return (
    <>
      <Box
        sx={{
          position: "relative",
          mt: 10,
          width: "100%",
          height: { xs: "50vh", md: "30vh" }, // Taller to accommodate content
          overflow: "hidden",
        }}
      >
        {/* Overlay */}
        <Box
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            backgroundColor: "rgba(0, 0, 0, 0.7)", // 70% black overlay
            zIndex: 1,
          }}
        />

        {/* Background Image */}
        <img
          src="/images/poshInterior.png"
          alt="Deal of the Week Background"
          style={{
            width: "100%",
            height: "100%",
            objectFit: "cover",
            objectPosition: "center",
          }}
        />

        {/* Content Overlay */}
        <Box
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            zIndex: 2,
            display: "flex",
            alignItems: "center",
            padding: { xs: "1rem", md: "2rem" },
          }}
        >
          <Container maxWidth="md">
            <Grid container spacing={2}>
              {/* Left Side: Special Image */}
              <Grid
                item
                xs={12}
                md={6}
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "flex-end",
                  float: "right",
                }}
              >
                <img
                  src={dealData.specialImage}
                  alt="Special Deal"
                  style={{
                    maxWidth: `${isTablet ? "30%" : isMobile ? "40%" : "70%"}`,
                    maxHeight: { xs: "40vh", md: "50vh" },
                    objectFit: "contain",
                  }}
                />
              </Grid>

              {/* Right Side: Title, Description, Countdown, Button */}
              <Grid
                item
                xs={12}
                md={6}
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "center",
                  alignItems: "center",
                  textAlign: { xs: "center", md: "left" },
                  color: "#fff",
                }}
              >
                {/* Title */}
                <Typography
                  variant={isMobile ? "h5" : "h3"}
                  sx={{ mb: 2, fontWeight: "bold" }}
                >
                  <span style={{ color: COLORS.themeYellow }}>DEAL</span> OF THE
                  WEEK
                </Typography>

                {/* Product Description */}
                <Typography
                  variant={isMobile ? "body2" : "body1"}
                  sx={{ mb: 3 }}
                >
                  {dealData.productDescription}
                </Typography>

                {/* Countdown Timer */}
                <Box
                  sx={{
                    display: "flex",
                    gap: "1rem",
                    mb: 3,
                    justifyContent: { xs: "center", md: "flex-start" },
                  }}
                >
                  <Box sx={{ textAlign: "center" }}>
                    <Typography
                      variant="h5"
                      sx={{ fontWeight: "bold", color: COLORS.themeYellow }}
                    >
                      {timeLeft.days.toString().padStart(2, "0")}
                    </Typography>
                    <Typography variant="caption">DAYS</Typography>
                  </Box>
                  <Box sx={{ textAlign: "center" }}>
                    <Typography
                      variant="h5"
                      sx={{ fontWeight: "bold", color: COLORS.themeYellow }}
                    >
                      {timeLeft.hours.toString().padStart(2, "0")}
                    </Typography>
                    <Typography variant="caption">HRS</Typography>
                  </Box>
                  <Box sx={{ textAlign: "center" }}>
                    <Typography
                      variant="h5"
                      sx={{ fontWeight: "bold", color: COLORS.themeYellow }}
                    >
                      {timeLeft.minutes.toString().padStart(2, "0")}
                    </Typography>
                    <Typography variant="caption">MINS</Typography>
                  </Box>
                  <Box sx={{ textAlign: "center" }}>
                    <Typography
                      variant="h5"
                      sx={{ fontWeight: "bold", color: COLORS.themeYellow }}
                    >
                      {timeLeft.seconds.toString().padStart(2, "0")}
                    </Typography>
                    <Typography variant="caption">SECS</Typography>
                  </Box>
                </Box>

                {/* Buy Now Button */}
                <Button
                  component={Link}
                  to="/deal-of-the-week"
                  variant="contained"
                  size={isMobile ? "small" : "medium"}
                  sx={{
                    backgroundColor: COLORS.themeYellow,
                    color: COLORS.darkText,
                    fontSize: isMobile
                      ? "var(--font-size-xs)"
                      : "var(--font-size-s)",
                    borderRadius: "2px",
                    "&:hover": {
                      backgroundColor: COLORS.themeYellow, // Maintain color on hover (or adjust if needed)
                      opacity: 0.9,
                    },
                  }}
                >
                  Buy Now
                </Button>
              </Grid>
            </Grid>
          </Container>
        </Box>
      </Box>
    </>
  );
};

export default DealOfTheWeek;
